import React, { useContext, useState } from 'react';
import {
  DialogContent,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  FormControl,
} from '@mui/material';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import {
  defaultDriverOrderFooter,
  defaultDriverOrderFooterV2,
  defaultDriverOrdersReportFieldsConfigV2,
  defaultDriverPickupFooter,
  defaultDriverPickupsReportFieldsConfig,
  defaultDriverReturnOrderFooterV2,
  defaultDriverReturnReportFieldsConfigV2,
  defaultMerchantFooter,
  defaultMerchantReportFieldsConfig,
  defaultMerchantReturnFooter,
  defaultReturnReportFieldsConfig,
} from '../report-builder/fields-config';
import {
  ReportProps,
  ReportDocument,
  IFieldConfig,
  IFooterRow,
  Table,
} from '../report-builder/report';
import DownloadIcon from '@mui/icons-material/Download';
import { ClearIcon } from '@mui/x-date-pickers';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { format } from 'date-fns';
import { AuthContext } from '../../../contexts/user-context/user-context';
import {
  DriverInvoiceType,
  IDriverInvoice,
} from '../../../interfaces/common/driver-invoices';
import { IMerchantOrdersInvoice } from '../../../interfaces/common/merchant-invoices';
import { MerchantType } from '../../../interfaces/delivery-company/merchants';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import { IOrderDeliveryTask, IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { Workflow } from '../../../enums/orders';
import CustomDialog from '../custom-dialog/custom-dialog';
import { OrderDeliveryTaskState } from '../../../enums/tasks';

interface InvoicePDFDialogProps {
  pdfData?: IDriverInvoice | IMerchantOrdersInvoice | null;
  onClose: () => void;
  merchant?: MerchantType;
  driver?: DriverType;
}

function InvoicePDFDialog<T extends OrderType | IPickupTask | IOrderDeliveryTask>({
  pdfData,
  onClose,
}: InvoicePDFDialogProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const { color } = useContext(OrgSettingsContext);
  const { user, config } = useContext(AuthContext);

  if (!pdfData) return <div>Failed to get invoice data</div>;

  const driver = (pdfData as IDriverInvoice).driver;
  const merchant = (pdfData as IMerchantOrdersInvoice).merchant;
  const formattedDate = format(
    pdfData.created_at,
    'dd-MM-yyyy'
  );
  let filename = '';
  if (driver) {
    filename = `${pdfData.id}.${driver.name}.${formattedDate}.pdf`;
  } else if (merchant) {
    filename = `${pdfData.id}.${merchant.name}.${formattedDate}.pdf`;
  }
  console.log('orders = ', pdfData.orders);

  const getMerchantReportDocProps = (): ReportProps<T> => {
    const tables: Table<T>[] = [];
    const standard_orders = pdfData.orders.filter(
      (order) => order.workflow == Workflow.STANDARD
    );
    const returnd_orders = pdfData.orders.filter(
      (order) => order.workflow == Workflow.RETURN
    );
    if (standard_orders.length > 0)
      tables.push({
        items: standard_orders as T[],
        fields_config: defaultMerchantReportFieldsConfig as IFieldConfig<T>[],
        header_bg: '#eee',
        title: 'Delivered Orders',
        table_footer_rows: defaultMerchantFooter as IFooterRow<T>[],
      });

    if (returnd_orders.length > 0)
      tables.push({
        items: returnd_orders as T[],
        fields_config: defaultReturnReportFieldsConfig as IFieldConfig<T>[],
        header_bg: '#eee',
        title: 'Returned Orders',
        table_footer_rows: defaultMerchantReturnFooter as IFooterRow<T>[],
      });

    return {
      color: color,
      tables: tables,
      subtitles: [`Merchant: ${merchant?.name} / ${merchant?.phone_number}`],
      organisation_id: user?.organisation?.org_id ?? 'ERROR',
      date_from: format(new Date(pdfData.created_at), 'dd-MM-yyyy'),
      date_to: format(new Date(pdfData.created_at), 'dd-MM-yyyy'),
      title: `Invoice #${pdfData.id}`,
    };
  };

  const getDriverOrderReportDocProps = (): ReportProps<T> => {
    const attempts = (pdfData as IDriverInvoice).delivery_attempts

    console.log("attempts = " , attempts)

    // handling successful attempts
    const standard_attempts = attempts.filter(att => att.workflow == Workflow.STANDARD)
    const sorted_standard_attempts = [...standard_attempts.filter(att => att.status == OrderDeliveryTaskState.COMPLETED), ...standard_attempts.filter(att => att.status == OrderDeliveryTaskState.FAILED)]



    const tables: Table<T>[] = [];
    if (sorted_standard_attempts.length > 0) {
      tables.push({
        items: sorted_standard_attempts as T[],
        fields_config:
          defaultDriverOrdersReportFieldsConfigV2 as IFieldConfig<T>[],
        header_bg: '#eee',
        title: 'Delivery Attempts',
        table_footer_rows: defaultDriverOrderFooterV2 as IFooterRow<T>[],
      });
    }


    // handling successful attempts
    const returned_attempts = attempts.filter(att => att.workflow == Workflow.RETURN)
    const sorted_returned_attempts = [...returned_attempts.filter(att => att.status == OrderDeliveryTaskState.COMPLETED), ...returned_attempts.filter(att => att.status == OrderDeliveryTaskState.FAILED)]

    if (returned_attempts.length > 0) {
      tables.push({
        items: sorted_returned_attempts as T[],
        fields_config: defaultDriverReturnReportFieldsConfigV2 as IFieldConfig<T>[],
        header_bg: '#eee',
        title: 'Returned Orders',
        table_footer_rows: defaultDriverReturnOrderFooterV2 as IFooterRow<T>[],
      });
    }
    return {
      color: color,
      tables: tables,
      subtitles: [`Driver: ${driver?.name} / ${driver?.phone_number}`],
      organisation_id: user?.organisation?.org_id ?? 'ERROR',
      date_from: format(new Date(pdfData.created_at), 'dd-MM-yyyy'),
      date_to: format(new Date(pdfData.created_at), 'dd-MM-yyyy'),
      title: `Delivery Invoice #${pdfData.id}`,
    };
  };

  const getDriverPickupReportDocProps = (): ReportProps<T> => {
    return {
      color: color,
      tables: [
        {
          items: (pdfData as IDriverInvoice).tasks as T[],
          fields_config:
            defaultDriverPickupsReportFieldsConfig as IFieldConfig<T>[],
          header_bg: '#eee',
          title: 'Pickup Tasks',
          table_footer_rows: defaultDriverPickupFooter as IFooterRow<T>[],
        },
      ],
      subtitles: [`Driver: ${driver?.name} / ${driver?.phone_number}`],
      organisation_id: user?.organisation?.org_id ?? 'ERROR',
      date_from: format(new Date(pdfData.created_at), 'dd-MM-yyyy'),
      date_to: format(new Date(pdfData.created_at), 'dd-MM-yyyy'),
      title: `Pickup Invoice #${pdfData.id}`,
    };
  };

  const reportDocProps: ReportProps<T> = merchant
    ? getMerchantReportDocProps()
    : (pdfData as IDriverInvoice).type === DriverInvoiceType.DELIVERY
      ? getDriverOrderReportDocProps()
      : getDriverPickupReportDocProps();

  console.log('report doc props = ', reportDocProps);

  return (
    <CustomDialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <div
        style={{
          textAlign: 'right',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <DialogTitle>Invoice Ready 🎉</DialogTitle>
        <IconButton
          onClick={() => {
            onClose && onClose();
            setIsOpen(false);
          }}
        >
          <ClearIcon color="primary" fontSize="large" />
        </IconButton>
      </div>

      <DialogContent style={(isDesktop || isTablet) ? { width: '100%', height: '80vh' } : {}}>
        {(isMobile && !isTablet) ? (
          <>
            {pdfData && (
              <PDFDownloadLink
                document={<ReportDocument {...reportDocProps} />}
                fileName={filename}
              >
                {({ loading } : {loading: boolean}) =>
                  loading ? (
                    <LinearProgress />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Button
                        size={(isMobile && !isTablet) ? 'small' : 'medium'}
                        variant="outlined"
                      >
                        Save to device{' '}
                        <DownloadIcon color={'primary'} fontSize="large" />
                      </Button>
                    </div>
                  )
                }
              </PDFDownloadLink>
            )}
          </>
        ) : (
          <>
            {pdfData && (
              <>
                <PDFDownloadLink
                  document={<ReportDocument {...reportDocProps} />}
                  fileName={filename}
                >
                  {({ loading } : {loading: boolean}) =>
                    loading ? (
                      <LinearProgress />
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <Button
                          size={(isMobile && !isTablet) ? 'small' : 'medium'}
                          variant="outlined"
                        >
                          Save to device
                          <DownloadIcon color={'primary'} fontSize="large" />
                        </Button>
                      </div>
                    )
                  }
                </PDFDownloadLink>
                <br />
                <PDFViewer style={{ width: '100%', height: '100%' }}>
                  <ReportDocument {...reportDocProps} />
                </PDFViewer>
              </>
            )}
          </>
        )}
      </DialogContent>
    </CustomDialog>
  );
}

export default InvoicePDFDialog;
